import faker from 'faker';
import React from 'react';
import Camera, { FACING_MODES, IMAGE_TYPES } from 'react-html5-camera-photo';
import 'react-html5-camera-photo/build/css/index.css';

import { makeStyles, Box, Step, StepContent, StepLabel, Stepper, Typography } from '@material-ui/core';

import * as MuiIcons from '@material-ui/icons';

import * as Elevated from '@pitaman71/omniglot-introspect';
import * as XLayout from '@pitaman71/react-explicit-layout';
import { Clients, Stores, Streams } from '@pitaman71/omniglot-live-data';
import { Domains } from '@pitaman71/omniglot-live-domains';

import { Modes } from '../../Views/OfControls';

import { Debug, Propose, Styler, Views } from '../../';

const __moduleName__ = 'omniglot-live-react.AsMedia';

let logEnable = false;

type Asset = Elevated.getValueType<typeof Domains.Media.AssetDomain>;

function TakePic(props: {
    zone: Stores.Zone,
    client?: Clients.ScalarClient<Asset>,
    pixelSize: { height: number, width: number },
    after: {
        commit: () => void,
        cancel: () => void
    }
}): JSX.Element {
    const useStyles = makeStyles((theme) => ({
        cameraContainer: {
            '& .react-html5-camera-photo': {
                display: 'flex'
            },
            ...props.pixelSize
        },
    }));
    const classes = useStyles();
    
    return (
        <Box className={classes.cameraContainer}>
        <Camera
            onTakePhoto = { (dataURI: string) => {
                if(props.client) {
                    props.client.assign({ uri: dataURI, pixelDimensions: props.pixelSize });
                    props.zone.commitAll().then(props.after.commit);
                }
            }}
            idealFacingMode = {FACING_MODES.USER}
            imageType = {IMAGE_TYPES.JPG}
            imageCompression = {0.97}
            idealResolution = {props.pixelSize}
            isImageMirror = {true}
            isSilentMode = {false}
            isDisplayStartCameraError = {true}
            isFullscreen = {false}
            isMaxResolution = {false}
        />
        </Box>
    );
}

function TakeVid(props: {
    zone: Stores.Zone,
    client?: Clients.ScalarClient<Asset>,
    pixelSize: { height: number, width: number },
    after: {
        commit: () => void,
        cancel: () => void
    },
    durationMs: number
}): JSX.Element {
    const videoRef = React.useRef<HTMLVideoElement|null>(null);
    const [ recorder, setRecorder ] = React.useState<MediaRecorder|undefined>()
    const [ mediaStream, setMediaStream ] = React.useState<MediaStream|undefined>();
    const recordedChunks:any[] = [];

    const stop = () => {
        //console.log(`END recording ${recordedChunks.length} chunks`);
        setRecorder(undefined);
    }
    React.useEffect(() => {
        if(videoRef.current) {
            const player = videoRef.current;
            navigator.mediaDevices
            .getUserMedia({audio: true, video: true})
            .then(mediaStream => {
                player.srcObject = mediaStream;
                setMediaStream(mediaStream);
            });
        }
    }, [videoRef.current]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <video ref={videoRef} style={{ 
                ...props.pixelSize,
                transform: "scaleX(-1)",
                WebkitTransform: "scaleX(-1)"
            }} onCanPlay={() => {
                videoRef.current?.play();
            }} autoPlay playsInline muted />
            <div style={{ width: props.pixelSize.width, display: 'flex', justifyContent: 'center' }}>
                <Styler.Button variant="outlined" onMouseDown={(event) => {
                    if(mediaStream) {
                        setRecorder(() => {
                            const mediaRecorder = new MediaRecorder(mediaStream, {mimeType: 'video/mp4'});

                            mediaRecorder.addEventListener('dataavailable', function(e) {
                            if (e.data.size > 0) {
                                recordedChunks.push(e.data);
                            }                    
                            });
                        
                            mediaRecorder.addEventListener('stop', function() {
                                stop();
                            });
                        
                            mediaRecorder.start()
                            //console.log('BEGIN recording')
                            return mediaRecorder;
                        })
                    }
                }} onMouseUp={(event) => {
                    recorder?.stop();
                }}>Record</Styler.Button>
            </div>
        </div>
    );
}

function readAsDataURL(file: File) {
    return new Promise<string|ArrayBuffer|null>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => {
            resolve(fr.result);
        }
        fr.onerror = () => {
            reject(fr.error);
        }
        fr.readAsDataURL(file)
    });
}

function getUploadURL(baseURI: string, path: string, size: number) {
    return fetch(baseURI+'/media/upload', {
        method: 'POST',
        mode: 'cors',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ path, size })
    })
    .then(response => response.json())
}

function getDownloadURL(baseURI: string, path: string) {
    return fetch(baseURI+'/media/download', {
        method: 'POST',
        mode: 'cors',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ path })
    })
    .then(response => response.json())
}

function uploadBuffer(signedURL: string, mime: string, buffer: ArrayBuffer) {
    return fetch(signedURL, { 
        method: 'PUT',
        headers: {
            "Content-Type": mime
        },
        body: buffer
    });
}

function makeDataUrl(blob: Blob) {
    return new Promise<string>((resolve, reject) =>{
        let reader = new FileReader();
        reader.onload = () => { resolve(reader.result as string) } ;
        reader.readAsDataURL(blob) ;
    });
}

function downloadBlob(signedURL: string) {
    return fetch(signedURL, { 
        method: 'GET',
        headers: {
            "Content-Type": "image/jpeg"
        }
    }).then(response => response.blob());
}
export function AsDownloader(props: {
    baseURI: string,
    style: React.CSSProperties,
    domain?: Elevated.Domain<Asset>,
    value?: Asset,
    client?: Clients.ScalarClient<Asset>
}) {
    return <_AsDownloader {...props}/>
}

function _AsDownloader(props: {
    baseURI: string,
    style: React.CSSProperties,
    domain?: Elevated.Domain<Asset>,
    value?: Asset,
    client?: Clients.ScalarClient<Asset>
}) {
    const [ dataURL, setDataURL ] = React.useState<string>();

    React.useEffect(() => {
        const words = props.value?.uri?.split('/') || [];

        if(words.length == 0) {
            // skip
        } else if(words[0] == '' && words[1] == 'images')
            setDataURL(props.value?.uri);
        else if(words[0] == '' && words[1] == 'media')
            getDownloadURL(props.baseURI, words[2])
            .then(({ signedUrl }) => downloadBlob(signedUrl))
            .then(blob => makeDataUrl(blob))
            .then(dataUrl_ => setDataURL(dataUrl_));
    }, [props.value]);

    const mime = !dataURL ? undefined : dataURL.substring(dataURL.indexOf(":")+1, dataURL.indexOf(";"));
    const src = dataURL === undefined ? undefined : dataURL.replace('quicktime', 'mp4');
    var isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    return (
        <Debug.Boundary name='Controls.Media._AsDownloader'>
            <React.Fragment>
                { !isSafari && !!mime && mime.split('/')[0] !== 'image' ? [] : <img style={{ display: 'block', maxWidth: '100%', ...props.style, objectFit: 'cover', objectPosition: 'center' }} src={src}/> }
                { isSafari || !mime || mime.split('/')[0] !== 'video' ? [] : <video style={{ display: 'block', maxWidth: '100%', ...props.style, objectFit: 'cover', objectPosition: 'center' }} src={src} autoPlay={true}/> }
            </React.Fragment>
        </Debug.Boundary>
    )
}

const Context = React.createContext<undefined|{
    suffix: string,
    imageFile?: File | undefined,
    setImageFile: React.Dispatch<React.SetStateAction<File | undefined>>,
    raw?: string,
    setRaw: React.Dispatch<React.SetStateAction<string | undefined>>,
    busy?: boolean,
    setBusy: React.Dispatch<React.SetStateAction<boolean>>,
}>(undefined);

export function Provide(props: React.PropsWithChildren<{}>) {
    const [ suffix, ] = React.useState(faker.datatype.uuid()+'.jpeg');
    const [ imageFile, setImageFile ] = React.useState<File>();
    const [ raw, setRaw ] = React.useState<string>();
    const [ busy, setBusy ] = React.useState(false);
    return <Context.Provider value={{
        suffix,
        imageFile, setImageFile,
        raw, setRaw,
        busy, setBusy 
    }}>{props.children}</Context.Provider>
}

class UploadManager {
    baseURI?: string;
    suffix?: string;
    asset: Asset;
    file?: File;
    numBytes?: number;
    buf?: ArrayBuffer;
    str?: string;

    constructor(asset: Asset) {
        this.asset = asset;
    }
    
    _readFile(file: File) {
        this.file = file;
        if(logEnable) console.log(`${this.suffix} : reading local media file into memory`);
        return new Promise<string|ArrayBuffer|null>((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => {
                resolve(fr.result);
            }
            fr.onerror = () => {
                reject(fr.error);
            }
            fr.readAsDataURL(file)
        }).then(dataURL => {
            this.str = dataURL === null ? undefined : dataURL instanceof ArrayBuffer ? new TextDecoder().decode(dataURL) : dataURL;
            this.numBytes = this.str === undefined ? undefined : this.str.length;
            return this.str;
        }).then(dataURL => {
            return dataURL === undefined ? undefined : fetch(dataURL).then(response => response.arrayBuffer());
        }).then(buf => {
            this.buf = buf;
        }).then(() => {
            if(this.asset === null) this.asset = {};
            this.asset.mime = !this.str ? undefined : this.str.substring(this.str.indexOf(":")+1, this.str.indexOf(";"));
            if(logEnable)  {
                console.log(`${this.suffix} : file size is ${this.numBytes}`);
                console.log(`${this.suffix} : mime type is ${this.asset.mime}`);
            }
            return this.buf;
        });
    }

    _getUploadURL() {
        const path = this.suffix;
        const size = this.buf?.byteLength;
        if(path === undefined) return Promise.reject('UploadManager.path must be set')
        if(size === undefined) return Promise.reject('UploadManager.buf must be set');
        if(logEnable) console.log(`${this.suffix} : requesting pre-signed upload URL from /media/upload`)
        return fetch(this.baseURI+'/media/upload', {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ path, size })
        })
        .then(response => response.json())
        .then(signedURL => {
            if(logEnable) console.log(`${this.suffix}  : received pre-signed upload URL from /media/upload ${signedURL}`)
            return signedURL;
        })
    }

    _readShape() {
        const mime = this.asset?.mime;
        const str = this.str;
        if(!mime) return Promise.reject('UploadManager.asset.mime must be set');
        if(!str) return Promise.reject('UploadManager.str must be set');
        const isImage = mime.split('/')[0] === 'image';
        const isVideo = mime.split('/')[0] === 'video';
        if(isImage) {
            if(logEnable) console.log(`${this.suffix} : reading metadata for image file`)
            return new Promise((resolve, reject) => {
                let img = new Image()
                img.src = str;
                img.onload = () => {
                    const pixelDimensions = { width: img.width, height: img.height };
                    if(logEnable) console.log(`${this.suffix} : dimensions: ${pixelDimensions}`);
                    this.asset = { ...this.asset, pixelDimensions };
                    resolve(pixelDimensions);
                };
                img.onerror = (err) => {
                    reject(err)
                }
            });
        } else if(isVideo) {
            return new Promise((resolve, reject) => {
                if(logEnable) console.log(`${this.suffix} : reading metadata for video file`)
                var video = document.createElement('video');

                video.onloadedmetadata = (event) => {
                    if(!this.asset) this.asset = {};
                    this.asset.pixelDimensions = { width: video.width, height: video.height };
                    if(logEnable) console.log(`${this.suffix} : dimensions: ${this.asset.pixelDimensions}`);
                    resolve(this.asset.pixelDimensions);
                };
                video.onerror = (err) => {
                    reject(err)
                };
                video.src = str;
            });
        }
    }
    _upload(signedURL: string) {
        const buffer = this.buf;
        const mime = this.asset?.mime;
        if(buffer === undefined) return Promise.reject('UploadManager.buf must be set');
        if(mime === undefined) return Promise.reject('UploadManager.asset.mime must be set');
        if(logEnable) console.log(`${this.suffix} : begin uploading media file`);
        return fetch(signedURL, { 
            method: 'PUT',
            headers: {
                "Content-Type": mime
            },
            body: buffer
        }).then(() => {
            if(logEnable) console.log(`${this.suffix} : done uploading media file`);
            if(!this.asset) this.asset = {};
            this.asset.uri = '/media/'+ this.suffix;
            return this.asset.uri;
        });
    }

    cancel() {        
    }

    static from_local(baseURI: string, file: File, client?: Clients.ScalarClient<Asset>) {
        const the = new UploadManager({});
        the.baseURI = baseURI;
        the.suffix = faker.datatype.uuid()+'.jpeg';
        the._readFile(file)
        .then(() => the._getUploadURL())        
        .then(({ signedUrl }) => the._upload(signedUrl))
        .then(( uri ) => client && client.assign(the.asset))
        return the;
    }

    static from_remote(asset: Asset) {
        const the = new UploadManager(asset);
        return the;
    }
}

export function AsMetadata(props: {
    asset?: Asset
}) {
    const pixelDimensions = props.asset?.pixelDimensions;
    const mime = props.asset?.mime;
    const isImage = mime === undefined && !!props.asset ? true : mime?.split('/')[0] === 'image';
    const isVideo = mime?.split('/')[0] === 'video';
    const empty = () => (<React.Fragment></React.Fragment>);

    return !pixelDimensions ? empty() : isVideo === true ? <p>Video {pixelDimensions?.width} x {pixelDimensions.height}</p> : isImage === true ? <p>Image {pixelDimensions?.width} x {pixelDimensions.height}</p> : empty();
}

export function AsUploader(props: {
    baseURI: string,
    style: React.CSSProperties,
    domain?: Elevated.Domain<Asset>,
    value?: Asset,
    client?: Clients.ScalarClient<Asset>
}) {
    const [ managers, setManagers ] = React.useState<UploadManager[]|undefined>(undefined);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const resetManagers = () => {
        if(props.value) {
            setManagers([UploadManager.from_remote(props.value)])
        } else {
            setManagers(undefined);
        }
    };
    const setFileList = (files: FileList|null) => {
        if(managers !== undefined) {
            managers.forEach(manager => manager.cancel());
        }
        if(!files) {
            resetManagers();
        } else {
            setManagers(Array.from(files).map((file, index) => UploadManager.from_local(props.baseURI, file, index === 0 ? props.client : undefined)));
        }
    };
    React.useEffect(() => {
        if(managers === undefined) {
            resetManagers();
        }
    }, [props.value])
    const [ index, setIndex ]= React.useState(0);
    var isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const manager = managers === undefined ? undefined : managers[index % managers.length];
    const asset = manager?.asset;
    const mime = asset?.mime;
    const isImage = mime === undefined && !!asset ? true : mime?.split('/')[0] === 'image';
    const isVideo = mime?.split('/')[0] === 'video';
    const local = manager?.str;
    const remote = manager?.asset;
    const style: React.CSSProperties = { display: 'block', objectFit: 'cover', objectPosition: 'center', ...props.style };
    const empty = () => (<React.Fragment></React.Fragment>);
    const buttonStyle: React.CSSProperties = { 
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(45, 45, 45, 0.1)'
    };

    const buttonLabel = local === undefined && remote === undefined ? "Upload" : "Replace";

    return (
        <XLayout.Center.Both>
            <div style={{ position: 'relative', width: '100%'}}>
                <XLayout.Center.Horizontal>
                    { local === undefined && remote !== undefined ? <_AsDownloader {...props} value={remote} style={style}/>: empty() }
                    { local === undefined ? empty() : (!isSafari && isImage === false) ? empty() : <img src={local} style={style}/> }
                    { local === undefined ? empty() : (isSafari || isVideo === false) ? empty() : <video src={local} style={style} autoPlay={true}/> }
                </XLayout.Center.Horizontal>
                <XLayout.Center.Horizontal style={{
                    position: 'absolute',
                    top: '90%',
                    left: '0%',
                    right: '100%'
                }}>
                    <AsMetadata asset={asset}/>                
                </XLayout.Center.Horizontal>
                <XLayout.Center.Horizontal style={{
                    position: 'absolute',
                    top: '80%',
                    left: '0%',
                    right: '100%'
                }}>
                    <input ref={inputRef} type="file" accept="image/*, video/mp4, .mp4" id="raised-button-file" style={{ display: 'none' }} onChange={(event) => {
                        setFileList(event.target.files);
                    }}/>
                    <label htmlFor="raised-button-file">
                        <Styler.Button variant="outlined" component="span" style={buttonStyle} startIcon={<MuiIcons.CloudUploadOutlined/>} mode={props.client === undefined ? 'disabled' : undefined}>{buttonLabel}</Styler.Button>
                    </label>
                </XLayout.Center.Horizontal>
            </div>
        </XLayout.Center.Both>
    );
}


export function AsDeleter(props: {
    view: Views.OfGoals.Plugin,
    baseURI: string,
    pixelDimensions: { width: number, height: number},
    value: Asset,
    on: { cancel: () => Promise<any>, confirm: () => Promise<any> }
}) {
    const useStyles = makeStyles((theme) => ({
        root: {
          width: '100%',
        },
        button: {
          marginTop: theme.spacing(1),
          marginRight: theme.spacing(1),
        },
        actionsContainer: {
          marginBottom: theme.spacing(2),
        },
        resetContainer: {
          padding: theme.spacing(3),
        },
    }));
    const classes = useStyles();
  
    return (
        <div style={{ position: 'absolute', height: '100%', overflowY: 'auto'}}>
        <Stepper activeStep={0} orientation="vertical">
          <Step key="step-delete">
            <StepLabel>{props.view.label}</StepLabel>
            <StepContent>
            <p><Typography variant="h5">{props.view.summary({ onClick: () => {} })}?</Typography></p>
            <Debug.Boundary name='Controls.Media.AsDeleter'>
                <XLayout.Center.Horizontal>
                    <_AsDownloader 
                        baseURI={props.baseURI}
                        style={{ display: 'block' }}
                        value={props.value}
                    />
                </XLayout.Center.Horizontal>
            </Debug.Boundary>
            <div className={classes.actionsContainer}>
                <Views.OfControls.AsRow  controls={[
                    {
                        class: Views.OfControls.Direction.Backward,
                        id: 'cancel',
                        label: () => 'Cancel',
                        icon: () => <MuiIcons.CancelOutlined/>,
                        onClick: props.on.cancel
                    },{
                        class: Views.OfControls.Direction.Forward,
                        id: 'remove',
                        label: () => 'Remove',
                        icon: () => <MuiIcons.CheckOutlined/>,
                        onClick: props.on.confirm
                    }
                ]} render={({ key, control, busy }) => <Views.OfControls.AsButton key={key} className={classes.button} control={control} busy={busy}/>} />
            </div>
            </StepContent>
          </Step>
        </Stepper>
        </div>
    )
}
